import { createImmerStore } from "@expert/state-utils";
import { freeze } from "immer";

export type AssistPanel = "guide" | "guidance";

export interface AssistPanelState {
    isGuideExpanded: boolean;
    isGuidanceExpanded: boolean;
    /** True while proactive guidance is collapsed only because the guide expanded over it. */
    isGuidanceCollapsedByGuide: boolean;
}

const DEFAULT_PANELS: AssistPanelState = freeze({
    isGuideExpanded: true,
    isGuidanceExpanded: true,
    isGuidanceCollapsedByGuide: false,
});

interface AssistPanelStore {
    sessions: Record<string, AssistPanelState>;
    expandPanel: (sessionId: string, panel: AssistPanel) => void;
    collapsePanel: (sessionId: string, panel: AssistPanel) => void;
    releaseGuide: (sessionId: string) => void;
    deleteSession: (sessionId: string) => void;
}

export const selectAssistPanels = (state: AssistPanelStore, sessionId: string): AssistPanelState =>
    state.sessions[sessionId] ?? DEFAULT_PANELS;

export const useAssistPanelStore = createImmerStore<AssistPanelStore>({
    devtools: {
        store: "assist-panel",
        name: "workspace/assist-panel",
        isProd: import.meta.env.VITE_ENV_TYPE === "production",
    },
})((set) => ({
    sessions: {},

    expandPanel: (sessionId, panel) =>
        set((state) => {
            const panels = { ...selectAssistPanels(state, sessionId) };
            if (panel === "guide") {
                panels.isGuidanceCollapsedByGuide = panels.isGuidanceExpanded || panels.isGuidanceCollapsedByGuide;
                panels.isGuideExpanded = true;
                panels.isGuidanceExpanded = false;
            } else {
                panels.isGuidanceExpanded = true;
                panels.isGuideExpanded = false;
                panels.isGuidanceCollapsedByGuide = false;
            }
            state.sessions[sessionId] = panels;
        }),

    collapsePanel: (sessionId, panel) =>
        set((state) => {
            const panels = { ...selectAssistPanels(state, sessionId) };
            if (panel === "guide") {
                panels.isGuideExpanded = false;
            } else {
                panels.isGuidanceExpanded = false;
                panels.isGuidanceCollapsedByGuide = false;
            }
            state.sessions[sessionId] = panels;
        }),

    releaseGuide: (sessionId) =>
        set((state) => {
            const panels = selectAssistPanels(state, sessionId);
            state.sessions[sessionId] = {
                isGuideExpanded: true,
                isGuidanceExpanded: panels.isGuidanceExpanded || panels.isGuidanceCollapsedByGuide,
                isGuidanceCollapsedByGuide: false,
            };
        }),

    deleteSession: (sessionId) =>
        set((state) => {
            delete state.sessions[sessionId];
        }),
}));
